import { merge } from 'lodash';
import { hexStringToRgb } from 'nucleus-core/colors/conversions';
import getStyleObjectWithPseudoState from './getStyleObjectWithPseudoState';

function getBackgroundColor({
  styleObject,
  customStyleSettings,
  colorPalette,
  // When `color` isn't defined in styleObject and customStyleSettings,
  // `baseColor` is used to calculate background color.
  // For example, when only opacity is changed in hover state,
  // there's no color value in styleObject and customStyleSettings.
  // So we use the color from the default state to get the final background color.
  baseColor
}) {
  const mergedStyleObject = merge({}, styleObject, customStyleSettings);
  if (!mergedStyleObject || !mergedStyleObject.background) {
    return {};
  }
  const { colorAlpha } = mergedStyleObject.background;
  let { color } = mergedStyleObject.background;
  color = color || baseColor;
  if (colorPalette && colorPalette[color]) {
    color = colorPalette[color];
  }

  let colorString;
  if (color) {
    const rgb = hexStringToRgb(color);
    const alpha = colorAlpha === undefined ? 1 : colorAlpha / 100;
    colorString =
      alpha === 1 ? color : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  } else {
    colorString = 'transparent';
  }
  return { backgroundColor: colorString };
}

export default function getBackground(
  styleObject,
  customStyleSettings,
  colorPalette,
  themeImages,
  imageLookup = {},
  supportsWebp
) {
  const mergedStyleObject = merge({}, styleObject, customStyleSettings);
  if (!mergedStyleObject || !mergedStyleObject.background) {
    return {};
  }
  const { image, borderRadius, color } = mergedStyleObject.background;

  const settings = {
    ...getBackgroundColor({ styleObject, customStyleSettings, colorPalette }),
    borderRadius,
    hover: getBackgroundColor({
      ...getStyleObjectWithPseudoState(styleObject, customStyleSettings, 'hover'),
      colorPalette,
      baseColor: color
    })
  };

  if (!image || !image.url || !image.url.assetUri) {
    return settings;
  }

  const { url, size, scale, fixedPosition, position, repeat } = image;
  settings.backgroundImage = `url(${
    themeImages && themeImages[url.assetUri]
      ? themeImages[url.assetUri]
      : (imageLookup[url.assetUri] && imageLookup[url.assetUri].hashedURL) || url.assetUri
  }${supportsWebp ? '?f=webp' : ''})`;

  // Parse size settings.
  if (size && size.width && size.height) {
    const { width, height } = size;
    settings.backgroundSize = `${width} ${height}`;
  } else if (scale || scale === undefined) {
    // If the scale setting is set to true or it is undefined, set the size to cover.
    settings.backgroundSize = 'cover';
  }

  if (fixedPosition) {
    settings.fixedPosition = true;
  }

  // Parse repeat settings.
  settings.backgroundRepeat = 'no-repeat';
  if (repeat) {
    if (repeat.x && repeat.y) {
      settings.backgroundRepeat = 'repeat';
    } else if (repeat.x && !repeat.y) {
      settings.backgroundRepeat = 'repeat-x';
    } else if (!repeat.x && repeat.y) {
      settings.backgroundRepeat = 'repeat-y';
    }
  }

  // Parse position settings.
  settings.backgroundPosition = 'left top';
  if (position) {
    const { xOffset, yOffset } = position;
    settings.backgroundPosition = `${xOffset || 'left'} ${yOffset || 'top'}`;
  }

  return settings;
}
